import { useState } from 'react';
import { Quote, MapPin, Star, ChevronLeft, ChevronRight, ExternalLink, CheckCircle2 } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useTranslation } from '../hooks/useTranslation';
import { GoogleIcon } from './Icons';

interface TestimonialData {
	id: string;
	quote: string;
	name: string;
	location: string;
	highlight: string;
	tripType: string;
	destination: string;
	rating: number;
}

const GOOGLE_REVIEWS_URL =
	'https://www.google.com/search?q=elephant+expeditions&sca_esv=657db2330b3edda8&biw=1920&bih=911&sxsrf=APpeQnsNa4LXR1-UCkSwvsn1MqZrt-v0JA%3A1786820598507&ei=9reAauLAHrqyhbIP-cGZmAE&ved=0ahUKEwjik7S4qaOWAxU6WUEAHflgBhMQ4dUDCBA&uact=5&oq=elephant+expeditions';

const TESTIMONIALS: TestimonialData[] = [
	{
		id: 'donatella',
		name: 'Donatella Chiappa',
		location: 'Switzerland',
		highlight: 'Flexibility, Remote Serenity, & Waterhole Encounters',
		tripType: 'Bespoke Self-Drive Expedition',
		destination: 'Kruger National Park (Talamati Bushveld Camp)',
		rating: 5,
		quote:
			'Our recent trip to Kruger National Park with Elephant Expeditions was beyond our expectations. From the moment we booked, everything went smoothly. The communication was clear, and all the details were taken care of with precision. We stayed at the beautiful Talamati Bushveld Camp. It’s tucked away in such a quiet, remote part of Kruger, and we really felt like we had the park to ourselves. \n\nThe self-catering option gave us the flexibility to enjoy the experience at our own pace, whether it was cooking dinner after a long day of game drives or enjoying the serenity of the savannah at dawn. The car rental process was seamless, and we had no issues navigating our way through the park. On top of the incredible sightings during the game drives, our bushveld camp provided unforgettable encounters. We witnessed elephants drinking from the waterhole, baboons in the trees, and we listened to the beautiful sounds of owls at night. All that truly added to the magic of the experience. \n\nThis was our second time visiting South Africa, and we’ve fallen even more in love with this country, thanks to the properly organised trip by Elephant Expeditions. Their attention to detail and seamless planning made it a stress-free, unforgettable adventure.',
	},
	{
		id: 'emanuela',
		name: 'Emanuela Donati',
		location: 'Switzerland',
		highlight: 'Magical Animal Encounters & Spectacular Night Safaris',
		tripType: 'Curated Wilderness Safari',
		destination: 'Kruger National Park & Lion Reserves',
		rating: 5,
		quote:
			'Our journey to Kruger National Park was nothing short of magical, thanks to Elephant Expeditions. Everything was arranged exactly as promised. We saw so many animals and, on top of the Big 5, we witnessed fascinating behaviour. Watching lions mating was something we’ll never forget! One of the highlights for us was the night safari—we were treated to an unforgettable sighting of two male lion brothers relaxing right next to our vehicle.\n\nElephant Expeditions not only made the whole trip easy but also brought us closer to the beauty and untamed majesty of Africa’s wildlife. This was only our second visit to South Africa, but it was an experience that we’ll carry with us forever. Elephant Expeditions not only made the whole trip easy but also brought us closer to the beauty and raw power of Africa’s wildlife. We definitely recommend them!',
	},
];

const getTestimonialKeys = (id: string) => {
	switch (id) {
		case 'donatella':
			return {
				tripType: 'tripBespokeSelfDrive',
				highlight: 'highlightFlexibility',
				destination: 'destTalamati',
			};
		case 'emanuela':
			return {
				tripType: 'tripCuratedWilderness',
				highlight: 'highlightMagical',
				destination: 'destLionReserves',
			};
		default:
			return {
				tripType: '',
				highlight: '',
				destination: '',
			};
	}
};

export default function Testimonials() {
	const [activeIndex, setActiveIndex] = useState(0);
	const { t } = useTranslation();

	const handleNext = () => {
		setActiveIndex(prev => (prev + 1) % TESTIMONIALS.length);
	};

	const handlePrev = () => {
		setActiveIndex(prev => (prev - 1 + TESTIMONIALS.length) % TESTIMONIALS.length);
	};

	const activeTesti = TESTIMONIALS[activeIndex];
	const activeKeys = getTestimonialKeys(activeTesti.id);
	const activeTripType = t(activeKeys.tripType as any) || activeTesti.tripType;
	const activeHighlight = t(activeKeys.highlight as any) || activeTesti.highlight;
	const activeDestination = t(activeKeys.destination as any) || activeTesti.destination;

	return (
		<section
			id='testimonials-section'
			className='bg-earth-cream-dark/40 py-24 border-t border-b border-earth-sand/30 relative'
		>
			<div className='absolute inset-0 bg-[radial-gradient(#b0a48d_1px,transparent_1px)] bg-size-[24px_24px] opacity-10 pointer-events-none'></div>

			<div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10'>
				{/* Header Block */}
				<div className='text-center max-w-2xl mx-auto mb-16 space-y-3'>
					{/* <span className='font-sans text-[10px] tracking-[0.3em] text-brand-gold-dark font-extrabold uppercase block'>
						{t('sovereignEncounters')}
					</span> */}
					<h2 className='font-serif text-3xl sm:text-4.5xl text-brand-forest font-bold tracking-tight'>
						{t('guestChronicles')}
					</h2>
					<div className='w-12 h-0.5 bg-brand-gold mx-auto my-3'></div>
					<p className='font-sans text-xs sm:text-sm text-brand-charcoal-light font-light leading-relaxed'>
						{t('guestChroniclesDesc')}
					</p>

					{/* Google Reviews Trust Badge in Header */}
					<div className='pt-2 flex items-center justify-center'>
						<a
							href={GOOGLE_REVIEWS_URL}
							target='_blank'
							rel='noopener noreferrer'
							className='inline-flex items-center space-x-2 bg-white px-3.5 py-1.5 rounded-full border border-earth-sand/35 shadow-xs hover:border-brand-gold/60 hover:shadow-md transition-all duration-200 group'
						>
							<GoogleIcon className='w-4 h-4' />
							<div className='flex space-x-0.5'>
								{[...Array(5)].map((_, i) => (
									<Star key={i} className='w-3 h-3 fill-brand-gold text-brand-gold-dark' />
								))}
							</div>
							<span className='font-sans text-[11px] font-semibold text-brand-forest'>5.0</span>
							<span className='font-sans text-[10.5px] text-brand-charcoal-light font-light'>• Google Reviews</span>
							<ExternalLink className='w-3 h-3 text-brand-gold-dark group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform' />
						</a>
					</div>
				</div>

				{/* Dynamic Display Layout: Side selection + Beautiful visual card */}
				<div className='grid grid-cols-1 lg:grid-cols-12 gap-12 items-start'>
					{/* Left panel: Compact index navigator list */}
					<div className='lg:col-span-4 space-y-4'>
						<span className='font-sans text-[9px] tracking-widest text-brand-gold-dark font-extrabold uppercase block mb-2 text-left sm:text-center lg:text-left'>
							{t('selectSafariStory')}
						</span>
						<div className='flex flex-col sm:flex-row lg:flex-col gap-3.5'>
							{TESTIMONIALS.map((testi, idx) => {
								const keys = getTestimonialKeys(testi.id);
								const tripType = t(keys.tripType as any) || testi.tripType;
								const highlight = t(keys.highlight as any) || testi.highlight;

								return (
									<button
										key={testi.id}
										onClick={() => setActiveIndex(idx)}
										className={`w-full text-left p-5 rounded-xl border transition-all duration-300 ease-in-out cursor-pointer flex flex-col justify-between ${
											activeIndex === idx
												? 'bg-brand-forest border-brand-forest text-white shadow-md scale-[1.01]'
												: 'bg-white border-earth-sand/30 hover:border-brand-gold text-brand-charcoal hover:bg-earth-cream/40'
										}`}
									>
										<div className='space-y-1.5 text-left'>
											<span
												className={`font-mono text-[9px] tracking-wider uppercase font-bold block ${activeIndex === idx ? 'text-brand-gold' : 'text-brand-gold-dark'}`}
											>
												{tripType}
											</span>
											<span className='font-serif text-sm font-bold tracking-tight leading-tight block'>{testi.name}</span>
											<p
												className={`font-sans text-[10.5px] font-light leading-snug truncate ${activeIndex === idx ? 'text-earth-sand-light' : 'text-brand-charcoal-light'}`}
											>
												{highlight}
											</p>
										</div>
										<div className='flex items-center justify-between mt-3.5 pt-2 border-t border-earth-sand/15 font-sans text-[10px]'>
											<span className='font-light opacity-90 block'>{testi.location}</span>
											<div className='flex space-x-0.5'>
												{[...Array(testi.rating)].map((_, i) => (
													<Star
														key={i}
														className={`w-3 h-3 fill-brand-gold ${activeIndex === idx ? 'text-brand-gold' : 'text-brand-gold-dark'}`}
													/>
												))}
											</div>
										</div>
									</button>
								);
							})}
						</div>

						{/* Google Reviews Card in Left Sidebar */}
						<div className='mt-5 p-4 rounded-xl bg-white border border-earth-sand/30 text-left space-y-3 shadow-xs'>
							<div className='flex items-center justify-between'>
								<div className='flex items-center space-x-2'>
									<GoogleIcon className='w-4.5 h-4.5' />
									<span className='font-serif text-xs text-brand-forest font-bold'>Google Reviews</span>
								</div>
								<div className='flex items-center space-x-1 text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full'>
									<CheckCircle2 className='w-3 h-3' />
									<span className='text-[9px] font-sans font-semibold uppercase'>Verified</span>
								</div>
							</div>
							<p className='font-sans text-[11px] text-brand-charcoal-light font-light leading-relaxed'>
								Read full authentic reviews and ratings directly on our verified Google profile.
							</p>
							<a
								href={GOOGLE_REVIEWS_URL}
								target='_blank'
								rel='noopener noreferrer'
								className='inline-flex items-center justify-between w-full px-3 py-2 rounded-lg bg-brand-forest/5 hover:bg-brand-forest hover:text-white text-brand-forest font-sans text-xs font-medium border border-brand-forest/15 transition-all duration-200 group'
							>
								<span>View on Google</span>
								<ExternalLink className='w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform' />
							</a>
						</div>
					</div>

					{/* Right panel: Expansive active story reading card (with transition) */}
					<div className='lg:col-span-8'>
						<AnimatePresence mode='wait'>
							<motion.div
								key={activeIndex}
								initial={{ opacity: 0, x: 20 }}
								animate={{ opacity: 1, x: 0 }}
								exit={{ opacity: 0, x: -20 }}
								transition={{ duration: 0.35, ease: 'easeInOut' }}
								className='bg-white border border-earth-sand/35 rounded-2xl p-8 sm:p-10 shadow-lg relative flex flex-col justify-between min-h-115 text-left'
							>
								{/* Visual Quote Overlay Decorator */}
								<div className='absolute top-6 right-8 text-earth-sand/20 pointer-events-none'>
									<Quote className='w-24 h-24 stroke-[1px] transform rotate-180' />
								</div>

								<div className='space-y-6 relative z-10'>
									{/* Category Pill Tag */}
									<div className='flex flex-wrap items-center gap-3'>
										<span className='bg-brand-forest/5 text-brand-forest border border-brand-forest/10 px-3.5 py-1 rounded-full text-[10px] tracking-widest uppercase font-bold'>
											{activeTripType}
										</span>
										<div className='flex items-center text-brand-gold-dark font-sans text-[10.5px] space-x-1'>
											<MapPin className='w-3.5 h-3.5' />
											<span className='font-medium'>{activeDestination}</span>
										</div>
									</div>

									{/* Refined Highlight Banner */}
									<h3 className='font-serif text-xl sm:text-2xl text-brand-forest font-bold leading-tight tracking-tight border-b border-earth-cream pb-4'>
										"{activeHighlight}"
									</h3>

									{/* Testimonial Quote Text with formatted paragraphs (left in English as requested) */}
									<div className='font-sans text-xs sm:text-[13px] text-brand-charcoal leading-relaxed font-light space-y-4 max-w-3xl whitespace-pre-line'>
										{activeTesti.quote}
									</div>
								</div>

								{/* Card Footer: Signature signature */}
								<div className='mt-8 pt-6 border-t border-earth-sand/20 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 relative z-10'>
									<div>
										<span className='font-serif text-base text-brand-forest font-bold block leading-none'>
											{activeTesti.name}
										</span>
										<span className='font-sans text-[10.5px] text-brand-gold-dark font-medium uppercase tracking-widest mt-1.5 block'>
											{t('verifiedAdventurer').replace('{location}', activeTesti.location)}
										</span>
									</div>

									{/* Prev/Next Quick Controls */}
									<div className='flex items-center space-x-2'>
										<button
											onClick={handlePrev}
											className='p-2 sm:p-2.5 rounded-lg border border-earth-sand/30 bg-white text-brand-forest hover:bg-earth-cream cursor-pointer transition-colors'
											title={t('prevReview')}
										>
											<ChevronLeft className='w-4 h-4' />
										</button>
										<span className='font-mono text-xs text-brand-charcoal-light font-bold min-w-10 text-center'>
											{activeIndex + 1} / {TESTIMONIALS.length}
										</span>
										<button
											onClick={handleNext}
											className='p-2 sm:p-2.5 rounded-lg border border-earth-sand/30 bg-white text-brand-forest hover:bg-earth-cream cursor-pointer transition-colors'
											title={t('nextReview')}
										>
											<ChevronRight className='w-4 h-4' />
										</button>
									</div>
								</div>
							</motion.div>
						</AnimatePresence>
					</div>
				</div>
			</div>
		</section>
	);
}
